import SEO from "@/common/components/seo";
import BoostIcon from "@/common/svgs/boost";
import DarkerMailIcon from "@/common/svgs/darker-mail";
import OpportuniesIcon from "@/common/svgs/opportunities";
import PresentIcon from "@/common/svgs/present";
import SalesIcon from "@/common/svgs/sales";
import { Button } from "@/components/ui/button";
import {
	Carousel,
	CarouselApi,
	CarouselContent,
	CarouselItem,
} from "@/components/ui/carousel";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import usePartners from "../hooks/use-partners";

function Header() {
	return (
		<div className="relative min-h-[calc(100vh)] overflow-hidden">
			<div
				className="flex h-screen flex-col items-center justify-center"
				style={{
					backgroundImage: "url(/images/header-background.png)",
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
				}}
			>
				<p className="web-heading-h1 z-3 text-center text-white max-sm:p-4">
					Become A Partner
				</p>
				<p className="web-body-b1 mt-4 max-w-[60ch] px-6 text-center text-white-80">
					Reach a passionate angler community and grow your business with Reel Rewards.
				</p>
			</div>
		</div>
	);
}

function WhyChooseUs() {
	return (
		<div className="flex flex-col gap-8 bg-gradient-to-br from-[#2edbca] via-[#004096] to-[#470096] px-4 py-16 max-sm:pb-25 md:-mt-18 md:gap-12 md:px-37 md:py-62">
			<div className="flex w-full">
				<div className="md:basis-1/3"></div>
				<span className="web-heading-h2 text-white md:basis-2/3">
					Why <span className="text-green-300">Choose Us</span> as Your Trusted
					Partner?
				</span>
			</div>

			<div className="flex w-full flex-col gap-6 md:flex-row md:gap-2">
				<p className="web-heading-h4 text-white md:basis-1/3">
					Partner with Us & Boost Your Business
				</p>
				<p className="text-white-80 web-body-b1 md:basis-2/3">
					Join a thriving community of fishing enthusiasts and businesses
					working together to create exciting opportunities. As a partner,
					you'll gain access to a dedicated audience, exclusive promotions, and
					unique collaborations that drive engagement and growth. Let's reel in
					success together!
					<br />
					<br />
					By partnering with us, you’ll connect with passionate anglers across
					Australia, boost your brand visibility, and offer exclusive deals that
					attract loyal customers. Whether you’re a retailer, service provider,
					or fishing tour operator, our platform helps you expand your reach and
					maximize your impact in the fishing community.
				</p>
			</div>
		</div>
	);
}

function Opportunity({
	icon,
	title,
	description,
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
}) {
	return (
		<div className="flex h-80 flex-col justify-between rounded-[32px] bg-white p-6 md:w-1/4 md:p-8">
			{icon}
			<div className="flex flex-col gap-4">
				<p className="web-heading-h4">{title}</p>
				<p className="web-body-b1 text-gray-700">{description}</p>
			</div>
		</div>
	);
}

function ReelInMoreOpportunities() {
	const [api, setApi] = useState<CarouselApi>();
	const [current, setCurrent] = useState(0);

	useEffect(() => {
		if (!api) {
			return;
		}

		setCurrent(api.selectedScrollSnap() + 1);

		api.on("select", () => {
			setCurrent(api.selectedScrollSnap() + 1);
		});
	}, [api]);

	const opportunities = [
		{
			icon: <OpportuniesIcon />,
			title: "Reel In More Opportunities",
			description:
				"Become a partner and tap into a dedicated audience of passionate anglers.",
		},
		{
			icon: <PresentIcon />,
			title: "Exclusive Giveaways",
			description:
				"Collaborate with us on exciting fishing-related giveaways and attract new customers.",
		},
		{
			icon: <SalesIcon />,
			title: "Increase Sales",
			description:
				"Our winners can redeem their prizes at your store, bringing in engaged and motivated buyers.",
		},
		{
			icon: <BoostIcon />,
			title: "Boost Your Visibility",
			description:
				"Gain extra exposure through social media promotions on our Instagram and Facebook pages.",
		},
	];

	return (
		<div
			className="-mt-5 flex flex-col gap-8 p-4 pt-20 md:-mt-30 md:gap-16 md:px-37 md:pt-60"
			style={{
				backgroundSize: "100% auto",
			}}
		>
			<div className="flex flex-col items-end justify-between gap-4 md:flex-row">
				<span className="web-heading-h2">
					Reel In{" "}
					<span
						style={{
							background:
								"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
							WebkitBackgroundClip: "text",
							backgroundClip: "text",
							WebkitTextFillColor: "transparent",
						}}
					>
						More Opportunities
					</span>
				</span>

				<p className="web-body-b1 max-w-100 text-gray-700">
					Become a partner and tap into a dedicated audience of passionate
					anglers.
				</p>
			</div>

			<Carousel setApi={setApi} className="p-4 md:hidden">
				<CarouselContent>
					{opportunities.map((opportunity) => (
						<CarouselItem>
							<Opportunity
								icon={opportunity.icon}
								title={opportunity.title}
								description={opportunity.description}
							/>
						</CarouselItem>
					))}
				</CarouselContent>
			</Carousel>

			<div className="flex items-center justify-center gap-2 md:hidden">
				{Array.from({ length: opportunities.length }).map((_, index) =>
					current === index + 1 ? (
						<div
							key={index}
							onClick={() => setCurrent(index)}
							className={`h-3 w-3 rounded-full bg-blue-700 transition-all duration-300`}
						></div>
					) : (
						<div
							key={index}
							onClick={() => setCurrent(index)}
							className={`bg-blue-900-20 h-2 w-2 rounded-full transition-all duration-300`}
						></div>
					)
				)}
			</div>

			<div className="flex flex-row justify-between gap-8 max-sm:hidden">
				{opportunities.map((opportunity) => (
					<Opportunity
						key={opportunity.title}
						icon={opportunity.icon}
						title={opportunity.title}
						description={opportunity.description}
					/>
				))}
			</div>
		</div>
	);
}

function PartnerBrandsWeWorkWith() {
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const { partners } = usePartners();

	return (
		<div className="relative pt-20 md:mt-20 md:p-9">
			<img
				src={"icons/star.png"}
				className="absolute top-100 right-6 h-12 w-12 rotate-32 transform md:hidden"
			/>
			<div
				className="flex flex-col gap-12 px-4 py-16 md:flex-row md:items-center md:justify-between md:rounded-[64px] md:px-29 md:py-54"
				style={{
					background:
						"radial-gradient(58.09% 54.69% at 29.83% 2.93%, rgba(46, 219, 202, 0.30) 0%, rgba(8, 50, 46, 0.00) 100%), radial-gradient(301.04% 189.4% at 89.12% -41.16%, #2EDBCA 4.81%, #004096 43.8%, #470096 100%)",
					backgroundBlendMode: "plus-lighter, normal",
				}}
			>
				<div className="flex max-w-130 flex-col gap-4 md:gap-12">
					<div className="flex flex-col gap-8">
						<span className="web-heading-h2 text-white">
							<span className="text-green-300">Partner Brands</span> We Work
							With
						</span>

						<p className="web-body-b1 text-white-80">
							Join a network of partners dedicated to delivering great
							experiences.
						</p>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-8">
					{partners.slice(0, 6).map((partner, index) => (
						<div
							key={`${partner.name}-${index}`}
							className="bg-light flex justify-center rounded-[32px] px-4 py-12 md:px-37"
						>
							<a href={partner.url} target="_blank">
								<img
									src={`${baseURL}/common/image/${partner.logoUrl}`}
									alt={partner.name}
									className="h-20 w-20 object-contain md:h-30 md:w-30"
								/>
							</a>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

const FormSchema = z.object({
	name: z.string().min(2).max(100),
	email: z
		.string({ message: "Email is required" })
		.email("Enter a valid email"),
	mobileNumber: z
		.string({ message: "Phone number is required" })
		.min(1)
		.max(15),
	businessName: z
		.string({ message: "Business name is required" })
		.min(4)
		.max(500),
});

function BecomeAPartner() {
	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
	});

	function onSubmit(data: z.infer<typeof FormSchema>) {
		const { name, email, mobileNumber, businessName } = data;

		const emailTo = encodeURIComponent("partners@reelrewards.com.au");
		const subject = encodeURIComponent(`Inquiry from ${name}`);
		const body = encodeURIComponent(
			`Name: ${name}\nEmail: ${email}\nMobile: ${mobileNumber}\nBusiness: ${businessName}`
		);

		const mailtoLink = `mailto:${emailTo}?subject=${subject}&body=${body}`;

		// This will open the default mail app
		window.location.href = mailtoLink;
	}

	return (
		<div className="flex flex-col-reverse gap-8 px-4 py-16 md:flex-row md:gap-25 md:px-63 md:py-50">
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="flex w-full flex-col gap-12 rounded-[32px] bg-white p-6 md:min-w-4/10 md:p-12"
				>
					<div className="grid gap-6">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="web-body-b1 text-gray-700">
										Full Name
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											className="bg-[rgba(0,_64,_150,_0.02)]"
											placeholder="Type your full name"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="web-body-b1 text-gray-700">
										Email address
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											className="bg-[rgba(0,_64,_150,_0.02)]"
											type={"email"}
											placeholder="Email"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="mobileNumber"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="web-body-b1 text-gray-700">
										Mobile Number
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											type={"tel"}
											placeholder="Enter mobile number"
											className="bg-[rgba(0,_64,_150,_0.02)]"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="businessName"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="web-body-b1 text-gray-700">
										Business Name
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											className="bg-[rgba(0,_64,_150,_0.02)]"
											type={"text"}
											placeholder="Type business name"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<Button className="flex justify-center md:w-60">Send</Button>
				</form>
			</Form>

			<div className="flex flex-col gap-8 md:gap-12">
				<span className="web-heading-h2">
					<span
						style={{
							background:
								"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
							WebkitBackgroundClip: "text",
							backgroundClip: "text",
							WebkitTextFillColor: "transparent",
						}}
					>
						Become a partner
					</span>{" "}
					Today
				</span>

				<span className="web-body-b2 text-gray-700">
					Fill out the form and join our growing network of fishing enthusiasts
					and exclusive brands.
					<br /> <br />
					By partnering with us, you’ll connect with a rapidly growing
					community, help secure exclusive promotions and giveaways for our
					members, and unlock exciting opportunities to engage with passionate
					anglers across Australia.
					<br />
					<br />
					Let’s work together to create memorable experiences and drive growth
					for your brand!
				</span>

				<div className="flex flex-col gap-5">
					<p className="web-body-b2-medium">Email Us</p>
					<span className="web-body-b1 flex flex-row gap-3 text-gray-700">
						<DarkerMailIcon />
						partners@reelrewards.com.au
					</span>
				</div>
			</div>
		</div>
	);
}

export default function BecomeAPartnerPage() {
	return (
		<div>
			<SEO
				title="Partners - Reel Rewards"
				description="Become a Reel Rewards partner and connect with a thriving community of fishing enthusiasts across Australia."
			/>
			<Header />
			<WhyChooseUs />
			<ReelInMoreOpportunities />
			<PartnerBrandsWeWorkWith />
			<BecomeAPartner />
		</div>
	);
}
